import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import Canvas from '../../layouts/dahboard_canvas';
import { useBaseUrl } from '../../../utils/utils';
import localForage from 'localforage';

type RefTab = "inbound" | "outbound" | "analytics";

interface Referral {
    id: number; patient_name: string; patient_id: string; direction: 'inbound' | 'outbound';
    partner_facility: string; referral_type: string; priority: string; status: string; created_at: string;
}

interface Stats {
    total_referrals: number; inbound_referrals: number; outbound_referrals: number;
    pending_referrals: number; accepted_referrals: number; rejected_referrals: number; completed_referrals: number;
}

const ReferralCenter: React.FC = () => {
    const [activeTab, setActiveTab] = useState<RefTab>('inbound');
    const [referrals, setReferrals] = useState<Referral[]>([]);
    const [stats, setStats] = useState<Stats | null>(null);
    const [loading, setLoading] = useState(true);
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();

    useEffect(() => { document.title = "Uzima - Referral Center"; loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = async () => {
        setLoading(true);
        try {
            const clinicId = await localForage.getItem('clinic_id');
            const token = await localForage.getItem('access_token');
            if (!clinicId) { setLoading(false); return; }
            const headers = token ? { 'Authorization': `Bearer ${token}` } : undefined;

            const [inRes, outRes, statsRes] = await Promise.all([
                fetch(`${baseUrl}facilities/referrals/inbound?clinic_id=${clinicId}`, { headers }),
                fetch(`${baseUrl}facilities/referrals/outbound?clinic_id=${clinicId}`, { headers }),
                fetch(`${baseUrl}facilities/referrals/statistics?clinic_id=${clinicId}`, { headers }),
            ]);

            const combined: Referral[] = [];
            if (inRes.ok) {
                const d = await inRes.json();
                (d.data || []).forEach((r: any) => combined.push({
                    id: r.id, patient_name: r.patient_name, patient_id: r.patient_id,
                    direction: 'inbound', partner_facility: r.referring_clinic,
                    referral_type: r.referral_type, priority: r.priority, status: r.status, created_at: r.created_at,
                }));
            }
            if (outRes.ok) {
                const d = await outRes.json();
                (d.data || []).forEach((r: any) => combined.push({
                    id: r.id, patient_name: r.patient_name, patient_id: r.patient_id,
                    direction: 'outbound', partner_facility: r.receiving_clinic,
                    referral_type: r.referral_type, priority: r.priority, status: r.status, created_at: r.created_at,
                }));
            }
            setReferrals(combined);

            if (statsRes.ok) {
                const d = await statsRes.json();
                setStats(d.data || d);
            }
        } catch (err) { console.error("Load error:", err); }
        finally { setLoading(false); }
    };

    const inbound = referrals.filter(r => r.direction === 'inbound');
    const outbound = referrals.filter(r => r.direction === 'outbound');
    const pending = referrals.filter(r => r.status === 'pending');

    const tabs: Array<{ key: RefTab; label: string; icon: string }> = [
        { key: "inbound", label: "Inbound", icon: "bx bx-log-in-circle" },
        { key: "outbound", label: "Outbound", icon: "bx bx-log-out-circle" },
        { key: "analytics", label: "Analytics", icon: "bx bx-bar-chart-alt-2" },
    ];

    const renderTable = (list: Referral[]) => list.length === 0 ? (
        <div className="sc-empty"><i className="bx bx-transfer"></i><h3>No Referrals</h3><p>Referrals will appear here when created.</p></div>
    ) : (
        <div className="sc-table-wrap"><table className="sc-table"><thead><tr><th>ID</th><th>Patient</th><th>Partner Facility</th><th>Type</th><th>Priority</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>{list.map(r => (<tr key={r.id}><td><span className="sc-chip soft">#{r.id}</span></td><td>{r.patient_name}</td>
                <td><strong>{r.partner_facility}</strong></td>
                <td><span className="sc-chip">{r.referral_type}</span></td>
                <td><span className={`sc-badge-status ${r.priority === 'emergency' ? 'expired' : r.priority === 'urgent' ? 'pending' : 'negotiating'}`}>{r.priority}</span></td>
                <td><span className={`sc-badge-status ${r.status === 'completed' ? 'agreed' : r.status === 'pending' ? 'pending' : 'negotiating'}`}>{r.status}</span></td>
                <td>{new Date(r.created_at).toLocaleDateString()}</td></tr>))}</tbody></table></div>
    );

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">
                        <div className="sc-header">
                            <div><h1>Referral Center</h1><p>Manage inbound and outbound facility referrals</p></div>
                            <button className="sc-btn sc-btn-primary" onClick={() => navigate('/institution/clinician/referrals')}>
                                <i className="bx bx-plus"></i> New Referral
                            </button>
                        </div>
                        <div className="sc-stats">
                            <div className="sc-stat"><div className="sc-stat-icon blue"><i className="bx bx-log-in-circle"></i></div><div className="sc-stat-value">{inbound.length}</div><div className="sc-stat-label">Inbound</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon teal"><i className="bx bx-log-out-circle"></i></div><div className="sc-stat-value">{outbound.length}</div><div className="sc-stat-label">Outbound</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon amber"><i className="bx bx-time-five"></i></div><div className="sc-stat-value">{pending.length}</div><div className="sc-stat-label">Pending</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon violet"><i className="bx bx-transfer"></i></div><div className="sc-stat-value">{referrals.length}</div><div className="sc-stat-label">Total</div></div>
                        </div>
                        <div className="sc-tabs">{tabs.map(t => (<button key={t.key} className={`sc-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}><i className={t.icon}></i> {t.label}{t.key === 'inbound' && inbound.length > 0 && <span className="sc-badge">{inbound.length}</span>}</button>))}</div>
                        {loading ? <div className="sc-spinner"></div> : (
                            <>
                                {activeTab === 'inbound' && (<div className="sc-panel"><div className="sc-panel-head"><h3>Inbound Referrals</h3></div><div className="sc-panel-body">{renderTable(inbound)}</div></div>)}
                                {activeTab === 'outbound' && (<div className="sc-panel"><div className="sc-panel-head"><h3>Outbound Referrals</h3></div><div className="sc-panel-body">{renderTable(outbound)}</div></div>)}
                                {activeTab === 'analytics' && (
                                    <div className="sc-panel">
                                        <div className="sc-panel-head"><h3>Referral Analytics</h3></div>
                                        <div className="sc-panel-body">
                                            {!stats || stats.total_referrals === 0 ? (
                                                <div className="sc-empty"><i className="bx bx-bar-chart-alt-2"></i><h3>No referral activity yet</h3><p>Analytics will populate once referrals are exchanged with other facilities.</p></div>
                                            ) : (
                                                <div className="sc-stats">
                                                    <div className="sc-stat"><div className="sc-stat-icon teal"><i className="bx bx-check-circle"></i></div><div className="sc-stat-value">{stats.accepted_referrals}</div><div className="sc-stat-label">Accepted</div></div>
                                                    <div className="sc-stat"><div className="sc-stat-icon amber"><i className="bx bx-time-five"></i></div><div className="sc-stat-value">{stats.pending_referrals}</div><div className="sc-stat-label">Pending</div></div>
                                                    <div className="sc-stat"><div className="sc-stat-icon blue"><i className="bx bx-check-double"></i></div><div className="sc-stat-value">{stats.completed_referrals}</div><div className="sc-stat-label">Completed</div></div>
                                                    <div className="sc-stat"><div className="sc-stat-icon violet"><i className="bx bx-x-circle"></i></div><div className="sc-stat-value">{stats.rejected_referrals}</div><div className="sc-stat-label">Rejected</div></div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <DashboardFooter />
            </div>
            <Canvas />
        </>
    );
};

export default ReferralCenter;
